import process from 'node:process';
import type { AstroCommandServices } from '@wix/cli-command-contract';
import type { CommandUnknownOpts } from '@commander-js/extra-typings';
import { createCommand, Option } from '@commander-js/extra-typings';
import { developmentSiteTemplateSchema } from '../dev-site-schema.js';
import type { DevSiteOperation } from '../non-interactive/dev-site.js';
import { validateDevelopmentSiteTemplate } from './validations.js';

export const devSiteCommand = (
  services: AstroCommandServices,
  cliVersion: string
) => {
  const machineOutput =
    services.loginMode === 'agent' || process.stdout.isTTY !== true;

  async function execute(
    command: CommandUnknownOpts,
    operation: DevSiteOperation
  ) {
    try {
      const { runCommand } = await import('./render-command.js');
      await runCommand(command, services, cliVersion, async (model) => {
        const { nonInteractiveDevSite } = await import(
          '../non-interactive/dev-site.js'
        );
        await nonInteractiveDevSite(
          services,
          cliVersion,
          model,
          operation,
          machineOutput
        );
      });
    } catch (error) {
      if (machineOutput) {
        const { logDevSiteError } = await import(
          '../non-interactive/dev-site.js'
        );
        logDevSiteError(error);
      }
      throw error;
    }
  }

  const list = createCommand('list')
    .description("List the account's sites")
    .option('--search <query>', 'Search sites by name or domain')
    .action(async (options, command) => {
      await execute(command, { type: 'list', search: options.search });
    });

  const select = createCommand('select')
    .description('Select a development site for the current app')
    .argument('<site-id>', 'The site id to select')
    .action(async (siteId, _options, command) => {
      await execute(command, { type: 'select', siteId });
    });

  const create = createCommand('create')
    .description('Create a development site')
    .addOption(
      new Option('--template <template>', 'Development site template')
        .argParser(validateDevelopmentSiteTemplate)
        .choices(developmentSiteTemplateSchema.options)
        .default('dev')
    )
    .option('--select', 'Select the created site for the current app')
    .action(async (options, command) => {
      await execute(command, {
        type: 'create',
        template: options.template,
        select: options.select === true,
      });
    });

  const current = createCommand('current')
    .description('Show the effective development site for the current app')
    .action(async (_options, command) => {
      await execute(command, { type: 'current' });
    });

  return createCommand('dev-site')
    .description('Manage the current app development site')
    .addCommand(list)
    .addCommand(select)
    .addCommand(create)
    .addCommand(current);
};
